import { Button, Input, Modal } from '@orobix/orobix-fe-components'
import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import { ErrorText } from '../../components/ErrorText/ErrorText'
import { OptimizationProcess } from '../../types'
import { post } from '../../utils/api'
import { useDataSyncStatus } from './IngestionStatusBanner/useDataSyncStatus'
import { useStyles } from './NewOptimizationProcessButton.styles'
import { ScenarioSelector } from './ScenarioSelector'

type PipelineType = 'from_last_config' | 'from_empty'

export const NewOptimizationProcessButton = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const [modal, setModal] = useState(false)
  const [pipelineType, setPipelineType] =
    useState<PipelineType>('from_last_config')
  const [importStartDate, setImportStartDate] = useState<string>('')
  const [importEndDate, setImportEndDate] = useState<string>('')
  const [ducts, setDucts] = useState<number | undefined>(7)
  const [dailySetupRowsMax, setDailySetupRowsMax] = useState<
    number | undefined
  >(2147483647)
  const [dailyRefillingsMax, setDailyRefillingsMax] = useState<
    number | undefined
  >(10000)
  const [rotationThresholdRows, setRotationThresholdRows] = useState<
    number | null
  >(null)
  const [selectedScenario, setSelectedScenario] = useState<string>(
    'Bilanciata & Max & Min'
  )
  // Applies to both pipelines. Empty input means null, which disables the
  // filter on the backend side; omitting it would default to 5 instead.
  const [qmaxMin, setQmaxMin] = useState<number | null>(5)
  // from_last_config only: minutes one gained order line and one avoided
  // refill are worth, prefilled with 0 (no time saving counted). Clearing a
  // field sends null: the run then has no time saving either.
  const [minutesSavedPerOrderLine, setMinutesSavedPerOrderLine] = useState<
    number | null
  >(0)
  const [minutesSavedPerRefill, setMinutesSavedPerRefill] = useState<
    number | null
  >(0)

  const { data: sync, isLoading } = useDataSyncStatus()

  const {
    mutate,
    isLoading: isCreating,
    error: createError,
  } = useCreateOptimizationProcess(setModal)

  // `sync` is the shared, polling useDataSyncStatus() query: its object
  // reference changes whenever a real sync run lands while the modal is
  // open, which would otherwise re-run this effect and silently overwrite
  // dates the user is mid-editing. The ref makes the prefill happen once per
  // modal opening — it is reset when the modal closes so the next opening
  // prefills again from whatever the sales window is by then.
  const hasPrefilledRef = useRef(false)

  useEffect(() => {
    if (!modal) {
      hasPrefilledRef.current = false
      return
    }

    if (
      !hasPrefilledRef.current &&
      sync?.salesStartDate &&
      sync?.salesEndDate
    ) {
      const [startIso] = new Date(sync.salesStartDate).toISOString().split('T')
      const [endIso] = new Date(sync.salesEndDate).toISOString().split('T')
      setImportStartDate(startIso)
      setImportEndDate(endIso)
      hasPrefilledRef.current = true
    }
  }, [modal, sync])

  const isDateRangePossible = (): boolean => {
    if (
      !importStartDate ||
      !importEndDate ||
      importStartDate === '' ||
      importEndDate === ''
    )
      return false
    return new Date(importEndDate) > new Date(importStartDate)
  }

  const isDateRangeSales = (): boolean => {
    if (!sync?.salesStartDate || !sync?.salesEndDate) {
      return true // Se non ci sono date di vendita, consideriamo valido
    }

    const salesStart = new Date(sync.salesStartDate)
    const salesEnd = new Date(sync.salesEndDate)
    const importStart = new Date(importStartDate)
    const importEnd = new Date(importEndDate)

    // Normalizza a mezzanotte per evitare problemi di fuso orario
    salesStart.setHours(0, 0, 0, 0)
    salesEnd.setHours(0, 0, 0, 0)
    importStart.setHours(0, 0, 0, 0)
    importEnd.setHours(0, 0, 0, 0)

    return importStart >= salesStart && importEnd <= salesEnd
  }

  const isDuctsValid = (): boolean => (ducts ?? 0) > 0

  const isDailySetupRowsMaxValid = (): boolean =>
    (dailySetupRowsMax ?? 0) > 0 && (dailySetupRowsMax ?? 0) <= 2147483647

  const isDailyRefillingsMaxValid = (): boolean =>
    (dailyRefillingsMax ?? 0) > 0 && (dailyRefillingsMax ?? 0) <= 2147483647

  const isRotationTresholdValid = (): boolean =>
    rotationThresholdRows === null || rotationThresholdRows > 0

  const isQmaxMinValid = (): boolean => qmaxMin === null || qmaxMin >= 1

  const isMinutesValid = (value: number | null): boolean =>
    value === null || value >= 0

  return (
    <>
      <Button
        label={t('New optimization process')}
        onClick={() => {
          setPipelineType('from_last_config')
          setModal(true)
        }}
        type="action"
        disabled={isLoading || !sync?.dataIngestionId}
      />

      {modal && (
        <Modal
          title={t('New optimization process')}
          onCloseRequest={() => {
            setModal(false)
          }}
          footer={
            <div className={classes.footerButtonWrapper}>
              <Button
                label={t('Cancel')}
                onClick={() => {
                  setModal(false)
                }}
              />
              <Button
                label={t('Proceed')}
                onClick={() => {
                  mutate({
                    pipelineType,
                    importStartDate,
                    importEndDate,
                    ducts,
                    dailySetupRowsMax,
                    dailyRefillingsMax,
                    rotationThresholdRows,
                    scenario: selectedScenario,
                    qmaxMin,
                    minutesSavedPerOrderLine,
                    minutesSavedPerRefill,
                  })
                }}
                type="action"
                loading={isCreating || isLoading}
                disabled={
                  isCreating ||
                  isLoading ||
                  !sync?.dataIngestionId ||
                  !isDateRangePossible() ||
                  !isDateRangeSales() ||
                  !isQmaxMinValid() ||
                  (pipelineType === 'from_last_config' &&
                    (!isMinutesValid(minutesSavedPerOrderLine) ||
                      !isMinutesValid(minutesSavedPerRefill))) ||
                  (pipelineType === 'from_empty' &&
                    (!isDuctsValid() ||
                      !isDailySetupRowsMaxValid() ||
                      !isDailyRefillingsMaxValid()))
                }
              />
            </div>
          }
        >
          <>
            <div className={classes.scrollableContent}>
              {sync?.dataIngestionId ? (
                <>
                  <div className={classes.inputContainer}>
                    <div className={classes.labelText}>
                      {t('Starting point')}
                    </div>
                    <div className={classes.pipelineGroup}>
                      <label
                        className={classes.pipelineOption}
                        htmlFor="pipelineType-from_last_config"
                      >
                        <input
                          id="pipelineType-from_last_config"
                          type="radio"
                          name="pipelineType"
                          value="from_last_config"
                          checked={pipelineType === 'from_last_config'}
                          onChange={() => setPipelineType('from_last_config')}
                        />
                        {t('Continue from last warehouse configuration')}
                      </label>
                      <label
                        className={classes.pipelineOption}
                        htmlFor="pipelineType-from_empty"
                      >
                        <input
                          id="pipelineType-from_empty"
                          type="radio"
                          name="pipelineType"
                          value="from_empty"
                          checked={pipelineType === 'from_empty'}
                          onChange={() => setPipelineType('from_empty')}
                        />
                        {t('Start from empty warehouse')}
                      </label>
                    </div>
                  </div>
                  {pipelineType === 'from_last_config' ? (
                    <div className={classes.pipelineDescription}>
                      {t('Continue from last warehouse description')}
                    </div>
                  ) : (
                    <div className={classes.descriptionText}>
                      <Trans i18nKey="Set a sales selection date limit and the number of channels to use in the optimization process" />
                    </div>
                  )}
                  <div className={classes.inputContainer}>
                    <div className={classes.labelText}>{t('Sales range')}</div>
                    <div className={classes.helpText}>
                      {t('Start import date')}
                    </div>
                    <Input
                      name="importStartDate"
                      id="importStartDate"
                      type="date"
                      required
                      value={importStartDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setImportStartDate(e.target.value)
                      }
                    />
                  </div>
                  <div className={classes.inputContainer}>
                    <div className={classes.helpText}>
                      {t('End import date')}
                    </div>
                    <Input
                      name="importEndDate"
                      id="importEndDate"
                      type="date"
                      required
                      value={importEndDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setImportEndDate(e.target.value)
                      }
                    />
                    {!isDateRangePossible() && (
                      <div className={classes.errorText}>
                        {t('End date must be greater than start date')}
                      </div>
                    )}
                    {isDateRangePossible() && !isDateRangeSales() && (
                      <div className={classes.dateRangeError}>
                        {t(
                          'Selected dates must be within the sales date range'
                        )}{' '}
                        {sync?.salesStartDate && sync?.salesEndDate && (
                          <span>
                            {new Date(sync.salesStartDate).toLocaleDateString(
                              'it-IT',
                              {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              }
                            )}{' '}
                            -{' '}
                            {new Date(sync.salesEndDate).toLocaleDateString(
                              'it-IT',
                              {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              }
                            )}
                          </span>
                        )}
                      </div>
                    )}
                    {sync?.salesStartDate && sync?.salesEndDate && (
                      <div className={classes.helpText}>
                        {t(
                          'Synchronized data available from {{ from }} to {{ to }}',
                          {
                            from: new Date(
                              sync.salesStartDate
                            ).toLocaleDateString('it-IT', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            }),
                            to: new Date(sync.salesEndDate).toLocaleDateString(
                              'it-IT',
                              {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              }
                            ),
                          }
                        )}
                      </div>
                    )}
                  </div>
                  <br />
                  <div className={classes.inputContainer}>
                    <div className={classes.labelText}>{t('Scenario')}</div>
                    <div className={classes.helpText}>{t('scenario info')}</div>
                    <ScenarioSelector
                      value={selectedScenario}
                      onChange={setSelectedScenario}
                      showLabel={false}
                    />
                  </div>
                  <br />
                  <div className={classes.inputContainer}>
                    <div className={classes.labelText}>{t('Qmax min')}</div>
                    <div className={classes.helpText}>{t('qmax min info')}</div>
                    <Input
                      name="qmaxMin"
                      id="qmaxMin"
                      type="number"
                      min={1}
                      step={1}
                      value={qmaxMin ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setQmaxMin(
                          e.target.value === '' ? null : Number(e.target.value)
                        )
                      }
                    />
                    {!isQmaxMinValid() && (
                      <div className={classes.qmaxMinError}>
                        {t('This value must be greater than zero')}
                      </div>
                    )}
                  </div>
                  {pipelineType === 'from_last_config' && (
                    <>
                      <div className={classes.inputContainer}>
                        <div className={classes.labelText}>
                          {t('Minutes saved per order line')}
                        </div>
                        <div className={classes.helpText}>
                          {t('Minutes saved per order line info')}
                        </div>
                        <Input
                          name="minutesSavedPerOrderLine"
                          id="minutesSavedPerOrderLine"
                          aria-label={t('Minutes saved per order line')}
                          type="number"
                          min={0}
                          step={0.1}
                          value={minutesSavedPerOrderLine ?? ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setMinutesSavedPerOrderLine(
                              e.target.value === ''
                                ? null
                                : Number(e.target.value)
                            )
                          }
                        />
                        {!isMinutesValid(minutesSavedPerOrderLine) && (
                          <div className={classes.qmaxMinError}>
                            {t('This value must be zero or greater')}
                          </div>
                        )}
                      </div>
                      <div className={classes.inputContainer}>
                        <div className={classes.labelText}>
                          {t('Minutes saved per refill')}
                        </div>
                        <div className={classes.helpText}>
                          {t('Minutes saved per refill info')}
                        </div>
                        <Input
                          name="minutesSavedPerRefill"
                          id="minutesSavedPerRefill"
                          aria-label={t('Minutes saved per refill')}
                          type="number"
                          min={0}
                          step={0.1}
                          value={minutesSavedPerRefill ?? ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setMinutesSavedPerRefill(
                              e.target.value === ''
                                ? null
                                : Number(e.target.value)
                            )
                          }
                        />
                        {!isMinutesValid(minutesSavedPerRefill) && (
                          <div className={classes.qmaxMinError}>
                            {t('This value must be zero or greater')}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {pipelineType === 'from_empty' && (
                    <>
                      <br />
                      <details className={classes.advancedSettings}>
                        <summary>{t('Advanced settings')}</summary>
                        <div className={classes.advancedSettingsContent}>
                          <div className={classes.inputContainer}>
                            <div className={classes.labelText}>
                              {t('Daily refillings max')}
                            </div>
                            <div className={classes.helpText}>
                              {t('Daily refillings max description')}
                            </div>
                            <Input
                              name="dailyRefillingsMax"
                              id="dailyRefillingsMax"
                              type="number"
                              required
                              min={1}
                              value={dailyRefillingsMax}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setDailyRefillingsMax(Number(e.target.value))
                              }
                            />
                            {!isDailyRefillingsMaxValid() && (
                              <div className={classes.errorText}>
                                {t(
                                  'This value must be greater than zero and less than 2147483647'
                                )}
                              </div>
                            )}
                          </div>
                          <br />
                          <div className={classes.inputContainer}>
                            <div className={classes.labelText}>
                              {t('Rotation threshold')}
                            </div>
                            <div className={classes.helpText}>
                              {t('rotation threshold info')}
                            </div>
                            <Input
                              name="rotationThresholdRows"
                              id="rotationThresholdRows"
                              type="number"
                              min={0.1}
                              step={0.1}
                              value={rotationThresholdRows ?? ''}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setRotationThresholdRows(
                                  e.target.value === ''
                                    ? null
                                    : Number(e.target.value)
                                )
                              }
                            />
                            {!isRotationTresholdValid() && (
                              <div className={classes.rotationThresholdError}>
                                {t('This value must be greater than zero')}
                              </div>
                            )}
                          </div>
                          <br />
                          <div className={classes.inputContainer}>
                            <div className={classes.labelText}>
                              {t('Daily setup rows max')}
                            </div>
                            <Input
                              name="dailySetupRowsMax"
                              id="dailySetupRowsMax"
                              type="number"
                              required
                              min={1}
                              value={dailySetupRowsMax}
                              disabled
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => setDailySetupRowsMax(Number(e.target.value))}
                            />
                            {!isDailySetupRowsMaxValid() && (
                              <div className={classes.errorText}>
                                {t(
                                  'This value must be greater than zero and less than 2147483647'
                                )}
                              </div>
                            )}
                          </div>
                          <br />
                          <div className={classes.inputContainer}>
                            <div className={classes.labelText}>
                              {t('Max ducts number')}
                            </div>
                            <Input
                              name="ducts"
                              id="ducts"
                              type="number"
                              required
                              min={1}
                              value={ducts}
                              disabled
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => setDucts(Number(e.target.value))}
                            />
                            {!isDuctsValid() && (
                              <div className={classes.errorText}>
                                {t('This value must be greater than zero')}
                              </div>
                            )}
                          </div>
                        </div>
                      </details>
                    </>
                  )}
                </>
              ) : (
                <div className={classes.noDataText}>
                  <Trans i18nKey="No available data to run optimization process" />
                </div>
              )}
            </div>
            {createError && (
              <ErrorText>
                {(createError as { response?: { data?: { error?: string } } })
                  ?.response?.data?.error ?? t('Could not create the process')}
              </ErrorText>
            )}
          </>
        </Modal>
      )}
    </>
  )
}

type OptimizationProcessResponse =
  | OptimizationProcess
  | { processes: OptimizationProcess[] }

const useCreateOptimizationProcess = (setModal: (value: boolean) => void) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation(
    ['optimization-processes', 'create'],
    ({
      pipelineType,
      importStartDate,
      importEndDate,
      ducts,
      dailySetupRowsMax,
      dailyRefillingsMax,
      rotationThresholdRows,
      scenario,
      qmaxMin,
      minutesSavedPerOrderLine,
      minutesSavedPerRefill,
    }: {
      pipelineType: PipelineType
      importStartDate: string
      importEndDate: string
      ducts?: number
      dailySetupRowsMax?: number
      dailyRefillingsMax?: number
      rotationThresholdRows?: number | null
      scenario: string
      qmaxMin: number | null
      minutesSavedPerOrderLine: number | null
      minutesSavedPerRefill: number | null
    }) =>
      post<OptimizationProcessResponse>(
        '/optimization-processes/',
        pipelineType === 'from_last_config'
          ? {
              pipeline_type: 'from_last_config',
              planning_start: importStartDate,
              planning_end: importEndDate,
              scenario,
              qmax_min: qmaxMin,
              minutes_saved_per_order_line: minutesSavedPerOrderLine,
              minutes_saved_per_refill: minutesSavedPerRefill,
            }
          : {
              pipeline_type: 'from_empty',
              planning_start: importStartDate,
              planning_end: importEndDate,
              ducts,
              daily_setup_rows_max: dailySetupRowsMax,
              daily_refillings_max: dailyRefillingsMax,
              rotation_threshold_rows: rotationThresholdRows,
              scenario,
              qmax_min: qmaxMin,
            }
      ),
    {
      retry: false,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['optimization-processes'])

        // Se la risposta contiene più processi (scenario "Bilanciata & Max & Min")
        if (data && 'processes' in data && Array.isArray(data.processes)) {
          toast.success(
            t(
              '3 optimization processes have been added to the processing queue'
            )
          )
        } else {
          toast.success(
            t(
              'A new optimization process has been added to the processing queue'
            )
          )
        }

        setModal(false)
      },
    }
  )
}
